import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Stack, CircularProgress, Alert, Grid, Card,
  CardContent, Avatar, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Tooltip, alpha, Divider, useTheme
} from '@mui/material';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import WalletIcon from '@mui/icons-material/Wallet';
import ImageIcon from '@mui/icons-material/Image';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';
import { fetchPendingDesignTransactions, approveDesignTransaction, rejectDesignTransaction } from '../designApi';

// ✅ Helper to safely extract an array from any API response structure
const extractArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.subscriptions)) return data.subscriptions;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.results)) return data.results;
  return [];
};

// ✅ Helper to safely get field values with fallbacks
const getField = (obj, ...fields) => {
  if (!obj) return null;
  for (const field of fields) {
    const parts = field.split('.');
    let val = obj;
    for (const part of parts) {
      val = val?.[part];
      if (val === undefined || val === null) break;
    }
    if (val !== undefined && val !== null) return val;
  }
  return null;
};

export default function DesignSubscriptions() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();

  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  // Receipt preview
  const [receiptDialog, setReceiptDialog] = useState({ open: false, imageUrl: '' });

  // Rejection dialog
  const [rejectDialog, setRejectDialog] = useState({ open: false, subscription: null });
  const [rejectionReason, setRejectionReason] = useState('');

  const loadPending = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchPendingDesignTransactions();
      console.log('📦 fetchPendingDesignTransactions response:', data);

      // ✅ Backend getPendingTransactions returns a direct array
      // Each item has mapSubscriptionResponse fields PLUS nested Admin, Plan, Wallet objects
      const subsArray = extractArray(data);
      console.log('📦 Extracted pending subscriptions:', subsArray.length, 'items');
      setSubscriptions(subsArray);
    } catch (err) {
      console.error('Failed to load pending subscriptions:', err);
      setError(err.response?.data?.message || 'Failed to load pending subscriptions');
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  // ✅ FIXED: Backend approveTransaction REQUIRES { adminId } in body
  // Also accepts adminName, notes
  const handleApprove = async (sub) => {
    const subId = getField(sub, 'Id', 'id');
    const subSubscriptionId = getField(sub, 'SubscriptionId', 'subscriptionId') || subId;

    if (!subId) {
      setError('Invalid subscription ID');
      return;
    }

    try {
      setActionLoading(subId);
      setError('');
      // ✅ Backend requires adminId in body
      const result = await approveDesignTransaction(subId, {
        adminId: user?.Id || user?.id,
        adminName: user?.Name || user?.name
      });
      console.log('✅ Approve result:', result);

      setSuccess(result?.message || `Subscription "${subSubscriptionId}" approved successfully`);
      await loadPending();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Approve error:', err);
      setError(err.response?.data?.message || 'Failed to approve subscription');
    } finally {
      setActionLoading(null);
    }
  };

  // ✅ FIXED: Backend rejectTransaction REQUIRES { adminId, rejectionReason } in body
  // Also accepts adminName, notes
  const handleReject = async () => {
    if (!rejectDialog.subscription) return;
    if (!rejectionReason.trim()) {
      setError('Rejection reason is required');
      return;
    }

    const sub = rejectDialog.subscription;
    const subId = getField(sub, 'Id', 'id');
    const subSubscriptionId = getField(sub, 'SubscriptionId', 'subscriptionId') || subId;

    if (!subId) {
      setError('Invalid subscription ID');
      return;
    }

    try {
      setActionLoading(subId);
      setError('');
      // ✅ Backend requires adminId AND rejectionReason
      const result = await rejectDesignTransaction(subId, {
        adminId: user?.Id || user?.id,
        adminName: user?.Name || user?.name,
        rejectionReason: rejectionReason.trim()
      });
      console.log('✅ Reject result:', result);

      setSuccess(result?.message || `Subscription "${subSubscriptionId}" rejected`);
      setRejectDialog({ open: false, subscription: null });
      setRejectionReason('');
      await loadPending();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Reject error:', err);
      setError(err.response?.data?.message || 'Failed to reject subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  const safeSubs = Array.isArray(subscriptions) ? subscriptions : [];

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha('#e65100', 0.08)} 0%, ${alpha('#f57c00', 0.08)} 100%)`,
          border: `1px solid ${alpha('#e65100', 0.15)}`
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: '#e65100', width: 50, height: 50 }}>
              <PendingActionsIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="#e65100">
                {t('PendingSubscriptions') || 'Pending Subscriptions'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('PendingSubsDesc') || 'Review and approve/reject design plan purchase requests'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`${safeSubs.length} ${t('Pending') || 'Pending'}`} color="warning" sx={{ fontWeight: 600 }} />
            <Tooltip title={t('Refresh') || 'Refresh'}>
              <IconButton onClick={loadPending} sx={{ bgcolor: alpha('#e65100', 0.1) }}>
                <RefreshIcon sx={{ color: '#e65100' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Empty State */}
      {safeSubs.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: `1px dashed ${alpha('#000', 0.2)}` }}>
          <PendingActionsIcon sx={{ fontSize: 64, color: alpha('#000', 0.15), mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('NoPendingSubscriptions') || 'No pending subscriptions'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('NoPendingSubsDesc') || 'All subscription requests have been processed'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {safeSubs.map((sub, index) => {
            // ✅ Backend getPendingTransactions returns:
            // mapSubscriptionResponse fields (flat): Id, SubscriptionId, AdminId, PlanName,
            //   NumberOfDesigns, Amount, Currency, CurrencySymbol, FormattedAmount,
            //   WalletName, WalletNumber, ReceiptImageUrl (FULL URL!), Status, CreatedAt, etc.
            // PLUS nested objects: Admin: { Id, Name, Number, BranchId }, Plan: {...}, Wallet: {...}
            const subId = getField(sub, 'Id', 'id');
            const subSubscriptionId = getField(sub, 'SubscriptionId', 'subscriptionId') || subId || '-';

            // ✅ FIXED: Admin name comes from nested Admin object (added after mapSubscriptionResponse)
            const subAdminName = getField(sub, 'Admin.Name', 'admin.Name') || `Admin #${getField(sub, 'AdminId', 'adminId') || '?'}`;
            const subAdminNumber = getField(sub, 'Admin.Number', 'admin.Number') || '';

            const subPlanName = getField(sub, 'PlanName', 'planName') || '-';
            const subDesigns = getField(sub, 'NumberOfDesigns', 'numberOfDesigns') || 0;

            // ✅ Use FormattedAmount from backend (mapSubscriptionResponse provides it)
            const subFormattedAmount = getField(sub, 'FormattedAmount', 'formattedAmount');
            const subAmount = getField(sub, 'Amount', 'amount') || 0;
            const subCurrency = getField(sub, 'Currency', 'currency') || 'YER';
            const displayAmount = subFormattedAmount || `${subAmount} ${subCurrency}`;

            // ✅ Flat fields from mapSubscriptionResponse
            const subWalletName = getField(sub, 'WalletName', 'walletName') || '-';
            const subWalletNumber = getField(sub, 'WalletNumber', 'walletNumber') || '';
            const subCreatedAt = getField(sub, 'CreatedAt', 'createdAt');

            // ✅ CRITICAL FIX: Receipt image field is "ReceiptImageUrl" (NOT "ReceiptImage")
            // mapSubscriptionResponse does: ReceiptImageUrl: getFullImageUrl(req, sub.ReceiptImage)
            // So it's already a FULL URL
            const subReceiptImageUrl = getField(sub, 'ReceiptImageUrl', 'receiptImageUrl');

            return (
              <Grid item xs={12} md={6} key={subId || index}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha('#e65100', 0.2)}`,
                    transition: 'all 0.3s',
                    '&:hover': { boxShadow: `0 4px 20px ${alpha('#e65100', 0.15)}` }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Subscription ID and Date */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Chip
                        label={subSubscriptionId}
                        size="small"
                        sx={{ bgcolor: alpha('#e65100', 0.1), color: '#e65100', fontWeight: 700, fontSize: '0.75rem' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(subCreatedAt)}
                      </Typography>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {/* Admin Info - ✅ FIXED: Use nested Admin.Name, Admin.Number */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <Avatar sx={{ bgcolor: alpha('#6a1b9a', 0.1), width: 36, height: 36 }}>
                        <PersonIcon sx={{ color: '#6a1b9a', fontSize: 20 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {subAdminName}
                        </Typography>
                        {subAdminNumber && (
                          <Typography variant="caption" color="text.secondary">
                            {subAdminNumber}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    {/* Plan Info */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <Avatar sx={{ bgcolor: alpha('#ad1457', 0.1), width: 36, height: 36 }}>
                        <DesignServicesIcon sx={{ color: '#ad1457', fontSize: 20 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{subPlanName}</Typography>
                        <Chip
                          icon={<ImageIcon sx={{ fontSize: 14 }} />}
                          label={`${subDesigns} designs`}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem', bgcolor: alpha('#9c27b0', 0.1), color: '#9c27b0' }}
                        />
                      </Box>
                    </Stack>

                    {/* Payment Info */}
                    <Paper sx={{ p: 1.5, bgcolor: alpha('#000', 0.02), borderRadius: 2, mb: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <WalletIcon sx={{ fontSize: 18, color: '#1565c0' }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">{subWalletName}</Typography>
                            {subWalletNumber && (
                              <Typography variant="body2" fontWeight={600}>{subWalletNumber}</Typography>
                            )}
                          </Box>
                        </Stack>
                        <Typography variant="h6" fontWeight={700} color="#ad1457">
                          {displayAmount}
                        </Typography>
                      </Stack>
                    </Paper>

                    {/* Receipt Image - ✅ FIXED: Use ReceiptImageUrl (already full URL) */}
                    {subReceiptImageUrl ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<ReceiptIcon />}
                        onClick={() => setReceiptDialog({ open: true, imageUrl: subReceiptImageUrl })}
                        sx={{
                          mb: 2,
                          textTransform: 'none',
                          borderColor: alpha('#000', 0.2),
                          color: 'text.primary',
                          borderRadius: 2,
                          '&:hover': { borderColor: '#1976d2', bgcolor: alpha('#1976d2', 0.05) }
                        }}
                      >
                        {t('ViewReceipt') || 'View Payment Receipt'}
                      </Button>
                    ) : (
                      <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, py: 0.5 }}>
                        <Typography variant="caption">
                          {t('NoReceiptUploaded') || 'No payment receipt uploaded'}
                        </Typography>
                      </Alert>
                    )}

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        startIcon={actionLoading === subId ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                        onClick={() => handleApprove(sub)}
                        disabled={!!actionLoading}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                      >
                        {t('Approve') || 'Approve'}
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          setRejectDialog({ open: true, subscription: sub });
                          setRejectionReason('');
                          setError('');
                        }}
                        disabled={!!actionLoading}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                      >
                        {t('Reject') || 'Reject'}
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Receipt Preview Dialog */}
      <Dialog
        open={receiptDialog.open}
        onClose={() => setReceiptDialog({ open: false, imageUrl: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('PaymentReceipt') || 'Payment Receipt'}
          <IconButton onClick={() => setReceiptDialog({ open: false, imageUrl: '' })}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {receiptDialog.imageUrl ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <img
                src={receiptDialog.imageUrl}
                alt="Payment Receipt"
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ReceiptIcon sx={{ fontSize: 48, color: alpha('#000', 0.15), mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('NoReceiptImage') || 'No receipt image available'}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection Reason Dialog */}
      <Dialog
        open={rejectDialog.open}
        onClose={() => {
          if (!actionLoading) {
            setRejectDialog({ open: false, subscription: null });
            setRejectionReason('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('RejectSubscription') || 'Reject Subscription'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('RejectSubDesc') || 'Please provide a reason for rejecting this subscription request.'}
          </Typography>
          {rejectDialog.subscription && (
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: alpha('#000', 0.03), borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  {getField(rejectDialog.subscription, 'SubscriptionId', 'subscriptionId') || '-'}
                </Typography>
                <Typography variant="body2" color="#ad1457" fontWeight={700}>
                  {getField(rejectDialog.subscription, 'FormattedAmount', 'formattedAmount') ||
                    `${getField(rejectDialog.subscription, 'Amount', 'amount') || 0} ${getField(rejectDialog.subscription, 'Currency', 'currency') || 'YER'}`
                  }
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {getField(rejectDialog.subscription, 'Admin.Name', 'admin.Name') || '-'} •{' '}
                {getField(rejectDialog.subscription, 'PlanName', 'planName') || '-'}
              </Typography>
            </Paper>
          )}
          <TextField
            fullWidth
            label={t('RejectionReason') || 'Rejection Reason'}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            rows={3}
            required
            placeholder="e.g. Invalid receipt, incorrect amount..."
            error={!rejectionReason.trim() && rejectionReason.length > 0}
            helperText={!rejectionReason.trim() && rejectionReason.length > 0 ? 'Reason is required' : ''}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setRejectDialog({ open: false, subscription: null });
              setRejectionReason('');
            }}
            disabled={!!actionLoading}
            sx={{ textTransform: 'none' }}
          >
            {t('Cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={!rejectionReason.trim() || !!actionLoading}
            startIcon={
              actionLoading === getField(rejectDialog.subscription, 'Id', 'id')
                ? <CircularProgress size={18} color="inherit" />
                : <CancelIcon />
            }
            sx={{ textTransform: 'none' }}
          >
            {t('ConfirmReject') || 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}